export const PLATFORM_SERVICE_WORKER_URL = "/sw.js";
export const PLATFORM_SERVICE_WORKER_OPTIONS = Object.freeze({
  scope: "/",
  updateViaCache: "none" as const,
});

type RegisterServiceWorker = (scriptUrl: string, options: RegistrationOptions) => Promise<unknown>;

export type PlatformServiceWorkerRegistrationEnvironment = {
  production: boolean;
  pageLoaded: boolean;
  register?: RegisterServiceWorker;
  onLoad: (listener: () => void) => () => void;
};

export function browserRegistrationEnvironment(
  production = import.meta.env.PROD,
): PlatformServiceWorkerRegistrationEnvironment {
  const serviceWorker = "serviceWorker" in navigator ? navigator.serviceWorker : undefined;

  return {
    production,
    pageLoaded: document.readyState === "complete",
    ...(serviceWorker
      ? {
          register: (scriptUrl: string, options: RegistrationOptions) =>
            serviceWorker.register(scriptUrl, options),
        }
      : {}),
    onLoad: (listener) => {
      window.addEventListener("load", listener, { once: true });
      return () => {
        window.removeEventListener("load", listener);
      };
    },
  };
}

export function schedulePlatformServiceWorkerRegistration(
  environment = browserRegistrationEnvironment(),
) {
  const { production, pageLoaded, register } = environment;

  if (!production || !register) {
    return () => undefined;
  }

  const registerAfterLoad = () => {
    void register(PLATFORM_SERVICE_WORKER_URL, PLATFORM_SERVICE_WORKER_OPTIONS).catch(() => {
      // PWA installation is progressive enhancement; the online shell remains usable.
    });
  };

  if (pageLoaded) {
    registerAfterLoad();
    return () => undefined;
  }

  return environment.onLoad(registerAfterLoad);
}
